import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { useFormikContext } from 'formik'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import { isActiveTier, isUserTier, UserTierDto } from '../../../config/finance/financeConfig'
import { CommitEventType } from '../../Input/FormattableNumberInput'
import UserCombobox from '../../Combobox/UserCombobox'
import FormikField from '../../Formik/FormikField'
import { SubComboboxProps } from '../../Combobox/BaseCombobox'
import { ActiveUserStatuses, UserListItemDto } from '../../../config/userConfig'
import * as Yup from 'yup'
import { TierDialogProps } from './TierDialog'
import { FileMutationDto } from '../../../config/fileConfig'
import useUsers from '../../../hooks/useUsers'
import { TierAction } from './TierList'
import { usePrevious } from '@fluentui/react-hooks'

export const userTierValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        userId: Yup.string().label(translate('file.edit.form.finance.rates.tier.userid')).nonNullable().required(),
        purchaseRate: Yup.number().label(translate('file.edit.form.finance.rates.tier.purchaserate')).nonNullable().required(),
        salesRate: Yup.number().label(translate('file.edit.form.finance.rates.tier.salesrate')).nonNullable().required(),
    })
}

export default function UserTierFormContainer(props: TierDialogProps<FileMutationDto<UserTierDto>, UserTierDto>): ReactElement {
    const { values, setFieldValue } = useFormikContext<UserTierDto>()
    const { users } = useUsers()
    const prevTierValues = usePrevious(values)

    useEffect(() => {
        const userTier = users
            ?.find(user => user.id === values.userId)
            ?.tiers?.filter(tier => isUserTier(tier))
            ?.filter(tier => isActiveTier(tier))
            ?.find(tier => tier.currency === values.currency)

        if (
            (userTier && prevTierValues && values.userId !== prevTierValues.userId) ||
            (prevTierValues === undefined && props.action === TierAction.ADD)
        ) {
            setFieldValue('salesRate', userTier?.salesRate)
            setFieldValue('purchaseRate', userTier?.purchaseRate)
        }
    }, [values.userId])

    return (
        <>
            <FormikField<SubComboboxProps<UserListItemDto>, UserTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.userid'}
                fieldName={'userId'}
                component={UserCombobox}
                componentProps={{
                    multiselect: false,
                    selectedOptions: [values.userId],
                    onOptionSelect: (_, data) => setFieldValue('userId', data.selectedOptions?.[0]),
                    filter: entity =>
                        (!props.parentValues.tiers?.find(tier => tier.userId === entity.id) && ActiveUserStatuses.includes(entity.status)) ||
                        entity.id === values.userId,
                }}
            />

            <FormikField<MoneyInputProps, UserTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.purchaserate'}
                fieldName={'purchaseRate'}
                component={MoneyInput}
                componentProps={{
                    currency: values?.currency,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`purchaseRate`, value)
                        }
                    },
                }}
            />

            <FormikField<MoneyInputProps, UserTierDto>
                orientation={'horizontal'}
                required
                labelKey={'file.edit.form.finance.rates.tier.salesrate'}
                fieldName={'salesRate'}
                component={MoneyInput}
                componentProps={{
                    currency: values?.currency,
                    onCommit: (eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`salesRate`, value)
                        }
                    },
                }}
            />
        </>
    )
}
